import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { VuiModal, VuiFormGroup, VuiButtonPrimary, VuiFlexItem, VuiSpacer } from "@vectara/vectara-ui";
import { AiOutlineEdit } from "react-icons/ai";
import { useLoginContext } from "../../utils/LoginContext";
import { Customer, CustomerStatus, getStatusLabel, getValidTransitions } from "../../utils/commonTypes";
import { changeCustomerState, getCustomerById } from "../../api/api";

interface ChangeCustomerStateModalProps {
  isOpen: boolean;
  customer: Customer;
  onClose: () => void;
  onUpdated: (customer: Customer) => void;
}

export const ChangeCustomerStateModal = ({ isOpen, customer, onClose, onUpdated }: ChangeCustomerStateModalProps) => {
  const { authToken } = useLoginContext();
  const validTargets = getValidTransitions(customer.status);
  const [target, setTarget] = useState<CustomerStatus | "">("");
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setTarget("");
      setReason("");
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    if (!target) return;
    setIsSubmitting(true);
    try {
      await changeCustomerState(authToken, customer.customerId, target, reason);
      const refreshed = await getCustomerById(authToken, customer.customerId);
      if (refreshed) {
        onUpdated(refreshed);
      }
      toast.success(`Customer state changed to ${getStatusLabel(target)}.`);
      onClose();
    } catch (error) {
      console.error("Error changing customer state:", error);
      toast.error(error instanceof Error ? error.message : "Failed to change customer state");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <VuiModal title="Change Customer State" isOpen={isOpen} onClose={onClose} icon={<AiOutlineEdit />}>
      <div className="text-sm">
        Current status: <strong>{getStatusLabel(customer.status)}</strong>
      </div>
      <VuiSpacer size="s" />
      {validTargets.length === 0 ? (
        <div className="text-sm">No state changes are available for this customer.</div>
      ) : (
        <>
          <VuiFormGroup label="Move to" labelFor="targetStatus">
            <div>
              {validTargets.map((status) => (
                <div key={status} style={{ marginBottom: "8px" }}>
                  <input
                    type="radio"
                    id={`target-${status}`}
                    name="targetStatus"
                    value={status}
                    checked={target === status}
                    onChange={() => setTarget(status)}
                    style={{ marginRight: "8px" }}
                  />
                  <label htmlFor={`target-${status}`}>{getStatusLabel(status)}</label>
                </div>
              ))}
            </div>
          </VuiFormGroup>

          <VuiFormGroup label="Reason (optional)" labelFor="reason">
            <textarea
              id="reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={3}
              style={{ width: "100%" }}
            />
          </VuiFormGroup>

          <VuiSpacer size="m" />

          <VuiFlexItem>
            <VuiButtonPrimary color="primary" onClick={handleConfirm} isDisabled={!target || isSubmitting}>
              Confirm
            </VuiButtonPrimary>
          </VuiFlexItem>
        </>
      )}
    </VuiModal>
  );
};

export default ChangeCustomerStateModal;
